import { addWeeks, isValid } from 'date-fns';
import { isBoolean, startCase } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { KeyValue } from '@angular/common';
import {
  Input,
  OnInit,
  Output,
  Component,
  OnDestroy,
  EventEmitter,
  ChangeDetectionStrategy,
  AfterViewInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { SnAbilityService } from '@lib/ability';
import { SnAlarm, SnAlarmStatus, SnCamera } from '@lib/api/entities';
import { SnAlarmsAPI, SnIntegrationsAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { SnAspectRatio } from '@lib/common/enums';
import { hasCoordinates } from '@lib/geo/helpers';
import { SnShareImageDialogComponent } from '@lib/ui/image-editor';
import { LprCameraStatsDialogComponent } from '@lib/vehicle-intelligence';
import {
  LprAggregationsAPI,
  LprPlateImageDialogComponent,
  RecentHitDialogComponent,
} from '@lib/vehicle-intelligence';

import { ALARM_TYPES } from '../../constants/alarm-types';
import { AlarmSendDialogComponent } from '../alarm-send-dialog';

@Component({
  selector: 'sn-alarm-card',
  templateUrl: './alarm-card.component.html',
  styleUrls: ['./alarm-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlarmCardComponent implements AfterViewInit, OnInit, OnDestroy {
  get hasAlarmPlayer(): boolean {
    const isLiveAlarmSource =
      this.alarm.source &&
      this.alarm.source instanceof SnCamera &&
      !!this.alarm.source.liveIsAvailable;

    return (
      !!this.alarm.recordingUrl ||
      !!this.alarm.thumbnailUrl ||
      isLiveAlarmSource ||
      !!this.recordingUrl
    );
  }

  get isClosed(): boolean {
    return this.alarm.status === SnAlarmStatus.Closed;
  }

  get isLprImage(): boolean {
    return !!this.alarm.details['Patch Image'] || !!this.alarm.thumbnailUrl;
  }

  @Input() public alarm: SnAlarm;
  @Input() public hideActions: boolean = false;
  @Output() public readonly dockCameras = new EventEmitter<SnAlarm>();
  @Output() public readonly goToAlarm = new EventEmitter<SnAlarm>();
  @Output() public readonly viewIncident = new EventEmitter<SnAlarm>();
  @Output() public readonly viewShotHistory = new EventEmitter<SnAlarm>();
  @Output() public readonly closeAlarmEvent = new EventEmitter<SnAlarm>();

  public readonly ASPECT_RATIO = SnAspectRatio;
  public readonly isInMap: boolean;
  public canChangeAlarms: boolean;

  public formattedTimeStart: number = null;

  public results: string[] = [];

  public recordingUrl: Object;
  public hasCoordinates: boolean;
  public isDroneSenseViewer = false;

  public isFlipped = false;

  public alarmIcon: string = null;

  public autoDockMaxCameras: number;

  public importantDetails: Record<string, string[]> = {
    'rt alpr': ['reason', 'Plate', 'list', 'plateState', 'make', 'model', 'color'],
  };

  public hiddenDetails: Record<string, string[]> = {
    'amber alert': [
      'isOpen',
      'isPreview',
      'messageList',
      'orgName',
      'timeZone',
      'personList',
      'targetList',
      'vehicleList',
    ],
    dronesense: ['videoUrl'],
  };

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _alarmsAPI: SnAlarmsAPI,
    private readonly _ability: SnAbilityService,
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _lprAggregationsAPI: LprAggregationsAPI,
    private readonly _router: Router,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _sanitizer: DomSanitizer,
  ) {
    this.autoDockMaxCameras = this._currentOrganization.settings.autoDockMaxCameras;
  }

  public ngOnInit(): void {
    if (this.alarm.recordingUrl && this.alarm.recordingUrlType) {
      let recordingUrl = this.alarm.recordingUrl;
      if (recordingUrl.indexOf('playlist_dvr.m3u8') > -1) {
        const start = this.alarm.createdAt;
        const startInSeconds = Math.floor(start.getTime() / 1000) - 30;
        recordingUrl = recordingUrl.split('.m3u8').join(`_range-${startInSeconds}-60.m3u8`);
      }
      this.recordingUrl = { [this.alarm.recordingUrlType]: recordingUrl };
    } else if (this.alarm.type === ALARM_TYPES.DRONE_SENSE) {
      const droneSenseVideoUrl = this.alarm.details?.sensors?.[0]?.videoUrl;
      if (droneSenseVideoUrl) {
        this.recordingUrl = this._sanitizer.bypassSecurityTrustResourceUrl(droneSenseVideoUrl);
        this.isDroneSenseViewer = true;
        this.isFlipped = true;
      } else {
        this.recordingUrl = null;
      }
    } else {
      this.recordingUrl = null;
    }

    if (!this.hasAlarmPlayer) {
      this.turn();
    }
    this.canChangeAlarms = this._ability.can({
      subject: 'alarms/alarm',
      action: ['change'],
    });
    this.hasCoordinates = hasCoordinates(this.alarm.geometry.coordinates);
  }

  public ngAfterViewInit(): void {
    this.determineCameraType();
  }

  public originalOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return 0;
  };

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public toStartCase(text: string): string {
    return startCase(text);
  }

  public isObject(item: any): boolean {
    return Object.prototype.toString.call(item).indexOf('Object') > -1;
  }

  public formatDataKeys(data: any[]): any[] {
    const formattedData: any = [];
    data.forEach((item: any) => {
      formattedData.push(
        Object.keys(item).reduce((accumulator, key) => {
          accumulator[key.toLowerCase()] = item[key];
          return accumulator;
        }, {}),
      );
    });
    return formattedData;
  }

  public isArrays(item: any): boolean {
    return Array.isArray(item);
  }

  public trackDetailItem(index: number, item: KeyValue<string, any>): any {
    return item.key;
  }

  public close(alarm: SnAlarm): void {
    this.closeAlarmEvent.next(alarm);
    this._alarmsAPI.closeAlarm(alarm.id).pipe(takeUntil(this._destroy$)).subscribe();
  }

  public turn(): void {
    this.isFlipped = !this.isFlipped;
  }

  public getImportantDetails(type: string): string[] {
    return this.importantDetails[type.toLowerCase()] || [];
  }

  public getHiddenDetails(type: string): string[] {
    return this.hiddenDetails[type.toLowerCase()] || [];
  }

  public canSeeDetail(type: string, key: string): boolean {
    return (
      this.getImportantDetails(type).indexOf(key) < 0 &&
      this.getHiddenDetails(type).indexOf(key) < 0
    );
  }

  public isTimestamp(value: any): boolean {
    if (isBoolean(value) || !value) {
      return false;
    }
    const date = new Date(value);
    return isValid(date);
  }

  public openAlarmGrid(): void {
    window.open(`/camera-grids/dynamic/alarms/${this.alarm.id}`, '_blank');
  }

  public openSendForm(alarm: SnAlarm): void {
    const dialogRef = this._matDialog.open(AlarmSendDialogComponent, { data: alarm });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        return result;
      });
  }

  public openRecentHits(alarm: SnAlarm): void {
    const point = alarm.geometry.coordinates.join();
    const radius = 1;
    const dialogRef = this._matDialog.open(RecentHitDialogComponent, {
      data: {
        cameraName: alarm.details['Camera Name'],
        createdTimestamp: alarm.createdAt,
        type: alarm.type,
        point,
        radius,
      },
      width: '600px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        return result;
      });
  }

  public openEnlargedImageDialog(): void {
    this._matDialog.open(LprPlateImageDialogComponent, {
      data: this.alarm,
      width: '45vw',
      panelClass: 'lpr-plate__dialog-container',
    });
  }

  public searchPlateReads(alarm: SnAlarm): void {
    this._router.navigate(['/plates/list/queries'], {
      queryParams: {
        date: 'month',
        search_reason: alarm.message,
        plate: alarm.details['plate'],
      },
    });
  }

  public openLPRStats(cameraName: string): void {
    const mode = 'day';
    let timeStart = new Date();
    timeStart = addWeeks(timeStart, -2);
    const formattedTimeStart = timeStart ? Math.floor(timeStart.getTime() / 1000) : null;
    const timeEnd = new Date();
    const formattedTimeEnd = timeEnd ? Math.floor(timeEnd.getTime() / 1000) : null;
    this._lprAggregationsAPI
      .getLprResultsPerCamera(cameraName, mode, formattedTimeEnd, formattedTimeStart)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        this._matDialog.open(LprCameraStatsDialogComponent, {
          data: {
            cameraName,
            results: res,
          },
          width: '75vw',
        });
      });
  }

  public determineCameraType(): void {
    switch (this.alarm.details.provider) {
      case 'FLOCK_SAFETY':
        this.alarmIcon = 'Icon_FS';
        break;
      case 'REKOR':
        this.alarmIcon = 'Icon_Rekor';
        break;
      case 'VIGILANT':
        this.alarmIcon = 'Icon_Vigilant';
        break;
      case ALARM_TYPES.DRONE_SENSE:
        this.alarmIcon = 'Icon_DroneSense';
        break;
      case 'GENETEC':
        this.alarmIcon = 'Icon_Genetec';
        break;
      case 'GENETEC_SHARP3':
        this.alarmIcon = 'Icon_Genetec';
        break;
      default:
        this.alarmIcon = null;
        break;
    }
  }

  public sendImg(): void {
    this._matDialog.open(SnShareImageDialogComponent, {
      data: {
        url: this.alarm.thumbnailUrl,
        alarm: this.alarm,
      },
    });
  }

  public alarmTypes(): boolean {
    return (
      this.alarm.type.toLowerCase().indexOf('lpr') > -1 ||
      this.alarm.type.toLowerCase().indexOf('minuteman') > -1
    );
  }
}
